import { motion } from "framer-motion";
import { memo } from "react";

import { TableColumnType } from "@/components/@types";
import { FieldCell } from "@/components/organisms/Field/FieldCell";
import {
  getGridCellBorderEdges,
  GRID_ROW_CONTAINER_CLASS,
  gridRowContainerStyle,
  ResolvedGridAppearance,
} from "@/components/organisms/Table/Table.tools";
import { useTableStore } from "@/components/organisms/Table/Table.store";
import { TableRowCheckbox } from "@/components/organisms/Table/TableRowCheckbox";
import { Values, ZIndex } from "@/constants";
import { cn } from "@/components/ui/utils";
import { CellProps, sanitizeKeyForCSS } from "@/hooks/useContextTree";
import { useSizing } from "@/hooks/useSizing";
import { getDefaultFieldCellPositionStyles } from "../Field/Field.utils";

const rowHeight = Values.DEFAULT_ROW_HEIGHT;

/**
 * Get the class name for a table row
 * @param contextId - The context ID
 * @param rowIndex - The row index
 * @param postfix - The postfix to add to the class name
 * @returns The class name
 */
export function getTableRowClassName(
  contextId: string,
  rowIndex: number,
  postfix: string,
) {
  return `${contextId}-row-${rowIndex}-${postfix}`;
}

const TableRow = memo(
  function TableRow<TableRowType extends { id: string }>({
    row,
    rowIndex,
    lockedColumns,
    unlockedColumns,
    onClick,
    contextId,
    hideOuterBorders = false,
    hideCheckboxes = false,
    isLastRow = false,
    isLoading,
    negativeScrollLeft,
    orderedColumnDefinitions,
    getCellProps,
    gridColors,
  }: {
    row: TableRowType;
    rowIndex: number;
    lockedColumns: TableColumnType[];
    unlockedColumns: TableColumnType[];
    onClick: (row: TableRowType) => void;
    contextId: string;
    hideOuterBorders?: boolean;
    hideCheckboxes?: boolean;
    isLastRow?: boolean;
    isLoading?: boolean;
    negativeScrollLeft: any; // MotionValue type
    orderedColumnDefinitions: Array<{
      objectKey: string;
      originalIndex?: number;
    }>;
    getCellProps: (opts: {
      rowIndex: number;
      colIndex: number;
      objectKey?: string;
    }) => CellProps;
    gridColors: ResolvedGridAppearance;
  }) {
    const isChecked = useTableStore((state) => state.checkedRows[row?.id]);
    const { cellStyles } = useSizing();
    const { minHeight, ...styles } = cellStyles;

    // Render locked columns
    const renderLockedCells = () => {
      return lockedColumns.map((col, lockedColIndex) => {
        const columnIndex = orderedColumnDefinitions.findIndex(
          (def) => def.objectKey === col.objectKey,
        );
        const isLastLockedColumn =
          lockedColIndex === lockedColumns.length - 1 &&
          unlockedColumns.length === 0;

        // Get cell props from useContextTree
        const cellProps = getCellProps({
          rowIndex,
          colIndex: columnIndex,
          objectKey: col.objectKey,
        });

        // Use sanitized key for offset variable
        const sanitizedKey = sanitizeKeyForCSS(col.objectKey);
        const offsetVar = `--${contextId}-column-${sanitizedKey}-offset`;
        const {
          alignItems: _alignItems,
          overflow: _overflow,
          ...positionStyle
        } = getDefaultFieldCellPositionStyles(col.type, "md") ?? {};
        const borderEdges = getGridCellBorderEdges({
          color: "var(--border)",
          isOuterRight: isLastLockedColumn,
          isOuterBottom: isLastRow,
          hideOuterEdges: hideOuterBorders,
        });

        return (
          <FieldCell
            key={`locked-${col.objectKey}-${rowIndex}`}
            type={col.type}
            getValue={col.getValue}
            getValueOptions={col.getValueOptions}
            objectKey={col.objectKey}
            data={row}
            isEditable={col?.isDisabled !== true}
            isSingleClick={true}
            isLoading={isLoading}
            isMultiline={col.isMultiline ?? false}
            hoverBackgroundColor={null}
            slotProps={{
              cell: {
                ...cellProps,
                // @ts-expect-error: data-test attributes are for testing purposes and not part of HTMLAttributes typing
                "data-testid": `table-row-${rowIndex}-locked-column-${columnIndex}-${col.objectKey}`,
                "data-grid-cell": true,
                // Its ground and its outer edges (a shadow) as classes: its
                // hover and focus change them. Its alignment and overflow are
                // the cell's own - what the type's position says of them is
                // left out, so focus still opens and lifts it.
                className: cn(
                  cellProps.className,
                  `${contextId}-row-${rowIndex}`,
                  "overflow-hidden bg-(--grid-row-bg)",
                  borderEdges.boxShadow && "[box-shadow:var(--cell-edges)]",
                ),
                style: {
                  ...styles,
                  ...positionStyle,
                  display: "flex",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  transform: `translateX(var(${offsetVar}))`,
                  willChange: "transform",
                  borderRight: borderEdges.borderRight,
                  borderBottom: borderEdges.borderBottom,
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  "--cell-edges": borderEdges.boxShadow,
                } as React.CSSProperties,
                radius: "0px",
                height: `${rowHeight}px`,
              },
            }}
          />
        );
      });
    };

    // Render unlocked columns
    const renderUnlockedCells = () => {
      return unlockedColumns.map((col, unlockedColIndex) => {
        const columnIndex = orderedColumnDefinitions.findIndex(
          (def) => def.objectKey === col.objectKey,
        );
        const isLastUnlockedColumn =
          unlockedColIndex === unlockedColumns.length - 1;

        // Get cell props from useContextTree
        const cellProps = getCellProps({
          rowIndex,
          colIndex: columnIndex,
          objectKey: col.objectKey,
        });

        // Use sanitized key for offset variable
        const sanitizedKey = sanitizeKeyForCSS(col.objectKey);
        const offsetVar = `--${contextId}-column-${sanitizedKey}-offset`;
        const {
          alignItems: _alignItems,
          overflow: _overflow,
          ...positionStyle
        } = getDefaultFieldCellPositionStyles(col.type, "md") ?? {};
        const borderEdges = getGridCellBorderEdges({
          color: "var(--border)",
          isOuterRight: isLastUnlockedColumn,
          isOuterBottom: isLastRow,
          hideOuterEdges: hideOuterBorders,
        });

        return (
          <FieldCell
            key={`${col.objectKey}-${rowIndex}`}
            type={col.type}
            getValue={col.getValue}
            getValueOptions={col.getValueOptions}
            objectKey={col.objectKey}
            data={row}
            isEditable={col?.isDisabled !== true}
            isSingleClick={true}
            isLoading={isLoading}
            isMultiline={false}
            isMultilineWhenFocusedOrEditing={col.isMultiline ?? false}
            hoverBackgroundColor={null}
            slotProps={{
              cell: {
                ...cellProps,
                // @ts-expect-error: data-test attributes are for testing purposes and not part of HTMLAttributes typing
                "data-testid": `table-row-${rowIndex}-unlocked-column-${columnIndex}-${col.objectKey}`,
                "data-grid-cell": true,
                // Its ground and its outer edges (a shadow) as classes: its
                // hover and focus change them. Its alignment and overflow are
                // the cell's own - what the type's position says of them is
                // left out, so focus still opens and lifts it.
                className: cn(
                  cellProps.className,
                  `${contextId}-row-${rowIndex}`,
                  "overflow-hidden bg-(--grid-row-bg)",
                  borderEdges.boxShadow && "[box-shadow:var(--cell-edges)]",
                ),
                style: {
                  ...styles,
                  ...positionStyle,
                  position: "absolute",
                  top: 0,
                  left: 0,
                  transform: `translateX(var(${offsetVar}))`,
                  willChange: "transform",
                  display: "flex",
                  borderRight: borderEdges.borderRight,
                  borderBottom: borderEdges.borderBottom,
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  "--cell-edges": borderEdges.boxShadow,
                } as React.CSSProperties,
                radius: "0px",
                height: `${rowHeight}px`,
              },
            }}
          />
        );
      });
    };

    return (
      <div
        data-testid={`table-row-${rowIndex}`}
        onClick={() => onClick(row)}
        className={GRID_ROW_CONTAINER_CLASS}
        onMouseEnter={() => {
          if (!isLoading && !hideCheckboxes) {
            const label = document.querySelector(
              `.${getTableRowClassName(contextId, rowIndex, "checkbox-label")}`,
            );
            if (label instanceof HTMLElement) {
              label.style.display = "none";
            }
            const checkbox = document.querySelector(
              `.${getTableRowClassName(contextId, rowIndex, "checkbox")}`,
            );
            // Its tick in the middle of the cell, as a checked row's is.
            if (checkbox instanceof HTMLElement) {
              checkbox.style.display = "flex";
            }
          }
        }}
        onMouseLeave={() => {
          if (!isChecked && !hideCheckboxes) {
            const checkbox = document.querySelector(
              `.${getTableRowClassName(contextId, rowIndex, "checkbox")}`,
            );
            if (checkbox instanceof HTMLElement) {
              checkbox.style.display = "none";
            }
            const label = document.querySelector(
              `.${getTableRowClassName(contextId, rowIndex, "checkbox-label")}`,
            );
            if (label instanceof HTMLElement) {
              label.style.display = "flex";
            }
          }
        }}
        style={{
          ...gridRowContainerStyle(gridColors),
          position: "absolute",
          left: 0,
          top: rowIndex * rowHeight,
          height: rowHeight,
          width: `var(--${contextId}-total-width)`,
          marginRight: "64px",
          zIndex: ZIndex.GRID.TABLE_ROW - rowIndex,
        }}
      >
        {/* Locked cells container */}
        <div
          style={{ position: "absolute", zIndex: ZIndex.GRID.ROW_LOCKED_CELLS }}
        >
          {!hideCheckboxes && (
            <TableRowCheckbox
              index={rowIndex}
              rowId={row?.id}
              loading={isLoading}
              contextId={contextId}
              hideOuterBorders={hideOuterBorders}
              isLastRow={isLastRow}
            />
          )}
          {renderLockedCells()}
        </div>

        {/* Unlocked cells container */}
        <motion.div
          style={{
            position: "absolute",
            left: 0,
            x: negativeScrollLeft,
            zIndex: ZIndex.GRID.ROW,
          }}
        >
          {renderUnlockedCells()}
        </motion.div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.row === nextProps.row &&
      prevProps.rowIndex === nextProps.rowIndex &&
      prevProps.hideOuterBorders === nextProps.hideOuterBorders &&
      prevProps.hideCheckboxes === nextProps.hideCheckboxes &&
      prevProps.isLastRow === nextProps.isLastRow &&
      prevProps.isLoading === nextProps.isLoading &&
      prevProps.lockedColumns === nextProps.lockedColumns &&
      prevProps.unlockedColumns === nextProps.unlockedColumns &&
      prevProps.orderedColumnDefinitions ===
        nextProps.orderedColumnDefinitions &&
      prevProps.gridColors === nextProps.gridColors
    );
  },
);

export { TableRow as Row };
